import React from 'react';
import PropTypes from 'prop-types';

// Loop-Schutz: pro Browser-Session genau ein automatischer Reload.
const RELOAD_GUARD_KEY = 'chunkReloadAttempted';

const isChunkLoadError = (error) => Boolean(
  error && (
    error.name === 'ChunkLoadError'
    || /Loading chunk \S+ failed/i.test(error.message || '')
  ),
);

/**
 * Faengt Render-Fehler der lazy-geladenen Routen ab (React.lazy + Suspense,
 * Code-Splitting Forts. 79). Wichtigster Fall: nach einem Deploy fordert ein
 * alter Client umbenannte Chunks an -> import() wirft ChunkLoadError. Dann EINMAL
 * automatisch reloaden (frischer index.html + Chunk-Manifest), per sessionStorage
 * gegen Reload-Schleifen abgesichert. Andere Fehler -> Fallback mit Reload-Button.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    const { reload } = this.props;
    if (isChunkLoadError(error)) {
      let alreadyTried = false;
      try {
        alreadyTried = window.sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
      } catch (e) {
        alreadyTried = false; // sessionStorage kann blockiert sein
      }
      if (!alreadyTried) {
        try {
          window.sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
        } catch (e) { /* ignore */ }
        reload();
      }
    }
  }

  handleReload() {
    const { reload } = this.props;
    try {
      window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch (e) { /* ignore */ }
    reload();
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            padding: '3rem 1rem',
            textAlign: 'center',
          }}
        >
          <p>Die Seite konnte nicht vollständig geladen werden.</p>
          <button type="button" onClick={this.handleReload}>
            Neu laden
          </button>
        </div>
      );
    }
    return children;
  }
}

ChunkErrorBoundary.propTypes = {
  children: PropTypes.node,
  // Injizierbar fuer Tests; Default = harter Browser-Reload.
  reload: PropTypes.func,
};

ChunkErrorBoundary.defaultProps = {
  children: null,
  reload: () => window.location.reload(),
};

export default ChunkErrorBoundary;
